/* ============================================================
   LÉMEIN — shop from the card
   ------------------------------------------------------------
   2026-08-25 rewrite. Rollback:
   assets/lemein-card-shop-2026-08-25-pre-cartgrid.txt

   Three jobs, one pass over Prestige's existing card markup:

   1. QUICK ADD. Prestige ships a 32x32 "Choose options" plus that
      opens the PDP. This replaces it with a real add: a 44x44
      control on touch / a full-width ADD bar on hover devices,
      which reveals the sizes of the currently selected colour in
      place and posts to /cart/add.js without leaving the grid.
      Single-variant products add on the first press.

   2. CARD GALLERY. Swipe on touch, chevrons on pointer devices,
      through that colour's own images in their existing order.
      2026-08-26: the homepage sets cells (.lm-set__cell) run the
      same gallery — see enhanceSet below.

   3. COLOUR. Choosing a swatch repaints the card image AND the
      gallery for that colour, and re-cuts the size list.

   COST. The previous build fetched /products/<handle>.js for every
   card on DOMContentLoaded -- 48 requests on /collections/all before
   the shopper had touched anything. Nothing is fetched here until
   the shopper shows intent (pointerenter / focusin / touchstart /
   pressing the control), except the first four cards which are
   warmed on idle so the top of the page is instant. Gallery slides
   are created on first use and only ever carry src for the current
   image and its two neighbours.

   NO SCROLL WORK. There is no scroll listener and no
   IntersectionObserver in this file, so the 2026-08-24 scroll-cost
   result (100 scroll steps = 1 layout, 5 style recalcs) is not
   touched.
   ============================================================ */

(() => {
  'use strict';

  const cache = new Map();

  /* ----------------------------------------------------------
     Image width bucket. Read once, off window.innerWidth, so no
     card ever forces a layout to size its own pictures.
     ---------------------------------------------------------- */
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const IMG_W = (() => {
    const w = window.innerWidth;
    const slot = w >= 1400 ? w / 3.6 : w >= 700 ? w / 3.2 : w / 2.1;
    const want = slot * dpr;
    const steps = [300, 400, 500, 600, 700, 800, 1000, 1200];
    return steps.find((s) => s >= want) || 1400;
  })();

  const sized = (url) => {
    if (!url) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + IMG_W;
  };

  /* 2026-09-04: same file regardless of host, size suffix or query — used to reuse the card's own
   already-decoded image for the first slide instead of fetching a second copy. */
const sameFile = (a, b) => {
  const p = (u) => {
    try { return new URL(u, location.origin).pathname.split('/').pop().replace(/_\d+x\d*(?=\.\w+$)/, '').toLowerCase(); }
    catch (e) { return String(u); }
  };
  return p(a) === p(b);
};
const TOUCH = window.matchMedia && window.matchMedia('(hover: none)').matches;

const announce = (msg) => {
    const el = document.getElementById('status-announcement');
    if (el) el.textContent = msg;
  };

  /* Promote the track to its own compositor layer only while it is actually
     being touched / hovered, and let it go 600ms after. Leaving will-change on
     across 48 cards would cost real memory. */
  const mkLive = (el) => {
    let h = 0;
    return (on) => {
      if (h) { clearTimeout(h); h = 0; }
      if (on) el.classList.add('is-live');
      else h = setTimeout(() => { el.classList.remove('is-live'); h = 0; }, 600);
    };
  };

  /* /products/<handle>.js measures 140-200ms even on a fast connection, which is
     longer than the gap between a finger landing and the same finger having
     travelled 8px. Warming at touchstart therefore cannot win that race on its
     own, so a card is warmed as it comes near the viewport instead: by the time
     it can be touched, the data has been sitting there for a second or more.
     Only cards actually scrolled to are fetched, and each is observed once. */
  const nearObs =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries, obs) => {
            entries.forEach((en) => {
              if (!en.isIntersecting) return;
              obs.unobserve(en.target);
              if (en.target._lmcWarm) en.target._lmcWarm();
            });
          },
          { rootMargin: '150px 0px' }
        )
      : null;

  function getProduct(handle) {
    if (!cache.has(handle)) {
      cache.set(
        handle,
        fetch('/products/' + handle + '.js')
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      );
    }
    return cache.get(handle);
  }

  /* ----------------------------------------------------------
     Model: split a product into one entry per colour, each with
     its own image list and its own size list.

     Images are matched off the media ALT TAG, not off position
     arithmetic. Prestige fakes per-variant galleries by writing
     "<free text> #<OptionName>_<OptionValue>" into every media
     alt, and all live multi-colourway products carry those tags,
     so the tag is the only reliable grouping. Products with no
     tags fall back to the whole image list.
     ---------------------------------------------------------- */
  function optionNames(product) {
    return (product.options || []).map((o) => (typeof o === 'string' ? o : o && o.name) || '');
  }

  function build(product) {
    const names = optionNames(product);
    const ci = names.findIndex((n) => /colou?r/i.test(n));
    const si = names.findIndex((n) => /size/i.test(n));
    const colourOptionName = ci >= 0 ? names[ci] : null;

    const allImages = (product.media || [])
      .filter((m) => m.media_type === 'image')
      .map((m) => ({ src: m.src || (m.preview_image && m.preview_image.src), alt: m.alt || '' }))
      .filter((m) => m.src);

    const imagesFor = (colourName) => {
      if (!colourOptionName || colourName === '__only__') return allImages.map((m) => m.src);
      const tag = ('#' + colourOptionName + '_' + colourName).toLowerCase();
      const hit = allImages.filter((m) => m.alt.toLowerCase().indexOf(tag) !== -1);
      return hit.length ? hit.map((m) => m.src) : [];
    };

    const order = [];
    const byName = new Map();

    (product.variants || []).forEach((v) => {
      const cName = ci >= 0 ? v.options[ci] : '__only__';
      if (!byName.has(cName)) {
        const entry = { name: cName, images: imagesFor(cName), sizes: [] };
        if (!entry.images.length) {
          const fi = v.featured_image;
          entry.images = fi && fi.src ? [fi.src] : allImages.map((m) => m.src);
        }
        byName.set(cName, entry);
        order.push(entry);
      }
      byName.get(cName).sizes.push({
        label: si >= 0 ? v.options[si] : v.title || 'Add',
        id: v.id,
        available: !!v.available
      });
    });

    return { colours: order, hasSize: si >= 0, single: (product.variants || []).length === 1 };
  }

  /* ----------------------------------------------------------
     Cart
     ---------------------------------------------------------- */
  async function addToCart(card, id, chip, note, title) {
    card.classList.add('lmc-busy');
    if (chip) chip.disabled = true;
    note.textContent = 'Adding';
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
      });
      if (!res.ok) throw new Error(String(res.status));
      if (chip) chip.classList.add('is-added');
      note.textContent = 'Added to bag';
      announce(title + ' added to bag');
      /* The header <cart-count> listens for this. */
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      return true;
    } catch (e) {
      note.textContent = 'Unavailable';
      announce('Could not add ' + title + ' to bag');
      return false;
    } finally {
      card.classList.remove('lmc-busy');
      if (chip) chip.disabled = false;
    }
  }

  /* ----------------------------------------------------------
     One card
     ---------------------------------------------------------- */
  function enhance(card) {
    if (card.dataset.lmc) return;
    card.dataset.lmc = '1';

    const figure = card.querySelector('.product-card__figure');
    const link = card.querySelector('a.product-card__media') || card.querySelector('a[href*="/products/"]');
    if (!figure || !link) return;

    const handleAttr = card.getAttribute('handle');
    const handle = (handleAttr || link.pathname.split('/products/')[1] || '').split('?')[0];
    if (!handle) return;

    const titleEl = card.querySelector('.product-title');
    const title = titleEl ? titleEl.textContent.trim() : handle;

    const primary = card.querySelector('.product-card__image--primary');
    const secondary = card.querySelector('.product-card__image--secondary');

    /* ---- overlay skeleton (no images, no requests) ---- */
    const lmc = document.createElement('div');
    lmc.className = 'lmc';

    const gal = document.createElement('div');
    gal.className = 'lmc__gal';
    const track = document.createElement('div');
    track.className = 'lmc__track';
    gal.appendChild(track);

    const chev = (d) =>
      '<svg viewBox="0 0 9 16" fill="none" aria-hidden="true"><path d="' +
      d +
      '" stroke="currentColor" stroke-width="1.2"/></svg>';
    /* The two chevrons are a POINTER affordance, the twin of the swipe, so
       they are deliberately out of the tab order. Putting them in would add
       96 tab stops to a 48-card grid before the footer, for a browse
       convenience that the PDP gallery already covers in full. Keyboard
       users step the same gallery with Left/Right while anything inside the
       card has focus (see the keydown handler below), and the arrow keys
       reveal the gallery exactly as a click does. */
    const prev = document.createElement('button');
    prev.type = 'button';
    prev.tabIndex = -1;
    prev.setAttribute('aria-hidden', 'true');
    prev.className = 'lmc__arrow lmc__arrow--prev';
    prev.innerHTML = chev('M7.5 1 1 8l6.5 7');
    const next = document.createElement('button');
    next.type = 'button';
    next.tabIndex = -1;
    next.setAttribute('aria-hidden', 'true');
    next.className = 'lmc__arrow lmc__arrow--next';
    next.innerHTML = chev('M1.5 1 8 8l-6.5 7');

    const dots = document.createElement('div');
    dots.className = 'lmc__dots';

    const open = document.createElement('button');
    open.type = 'button';
    open.className = 'lmc__open';
    open.setAttribute('aria-expanded', 'false');
    open.innerHTML =
      '<span class="sr-only">Add ' +
      title +
      ' to bag</span><span class="lmc__lbl" aria-hidden="true">Add</span>' +
      '<svg class="lmc__plus" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M6 0v12M0 6h12" stroke="currentColor" stroke-width="1.4"/></svg>';

    const panel = document.createElement('div');
    panel.className = 'lmc__panel';
    const sizes = document.createElement('div');
    sizes.className = 'lmc__sizes';
    const foot = document.createElement('div');
    foot.className = 'lmc__foot';
    const note = document.createElement('span');
    note.className = 'lmc__note';
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'lmc__close';
    close.innerHTML = '<span class="sr-only">Close sizes</span>&#10005;';
    foot.append(note, close);
    panel.append(sizes, foot);

    lmc.append(gal, prev, next, dots, open, panel);
    figure.appendChild(lmc);

    /* ---- state ---- */
    let model = null;
    let colour = 0;
    let idx = 0;
    let slides = [];
    let built = false;
    let panelOpen = false;
    const setLive = mkLive(lmc);

    const list = () => (model && model.colours[colour] ? model.colours[colour].images : []);

    /* ---- data, fetched on intent only ---- */
    let loading = null;
    function load() {
      if (model) return Promise.resolve(model);
      if (!loading) {
        loading = getProduct(handle).then((p) => {
          if (!p || !p.variants || !p.variants.length) return null;
          model = build(p);
          const inputs = [...card.querySelectorAll('fieldset input[type="radio"]')];
          if (inputs.length === model.colours.length) {
            inputs.forEach((input, i) => {
              if (input.checked) colour = i;
              const onPick = () => {
                if (colour === i) return;
                colour = i;
                idx = 0;
                paintCard();
                if (built) rebuildSlides();
                if (panelOpen) paintSizes();
              };
              input.addEventListener('change', onPick);
              const label = input.nextElementSibling;
              if (label) label.addEventListener('click', () => setTimeout(onPick, 0));
            });
          }
          markGallery();
          return model;
        });
      }
      return loading;
    }

    function markGallery() {
      if (list().length > 1) {
        lmc.classList.add('has-gal');
        /* Paint the dots as soon as we know there is more than one image, not
           on first use — they are the only cue that the card HAS a gallery,
           and on touch there is no arrow to give it away. */
        paintDots();
      } else {
        lmc.classList.remove('has-gal');
        dots.textContent = '';
      }
    }

    /* Repaint Prestige's own two <img> for the chosen colour so the
       resting card is right even if the gallery is never opened. */
    function paintCard() {
      const imgs = list();
      if (!imgs.length) return;
      if (primary && imgs[0]) {
        primary.removeAttribute('srcset');
        primary.removeAttribute('sizes');
        primary.src = sized(imgs[0]);
      }
      if (secondary && imgs[1]) {
        secondary.removeAttribute('srcset');
        secondary.removeAttribute('sizes');
        secondary.src = sized(imgs[1]);
      }
      markGallery();
    }

    /* ---- gallery ---- */
    function rebuildSlides() {
      track.textContent = '';
      slides = list().map((src, i) => {
        const s = document.createElement('div');
        s.className = 'lmc__slide';
        const im = document.createElement('img');
        /* The current slide and its two neighbours are the ones that can be on
           screen within one step, so they load for real. Everything past that
           stays lazy. */
        im.loading = Math.abs(i - idx) <= 1 ? 'eager' : 'lazy';
        im.decoding = 'async';
        im.alt = '';
        im.dataset.src = (i === idx && primary && primary.currentSrc && sameFile(primary.currentSrc, src)) ? primary.currentSrc : sized(src);
        s.appendChild(im);
        track.appendChild(s);
        return im;
      });
      idx = Math.min(idx, Math.max(0, slides.length - 1));
      paintDots();
      goto(idx, true);
    }

    function ensureGal() {
      if (built) return;
      built = true;
      rebuildSlides();
      card.classList.add('lmc-on');
      /* Slide 0 is the very image the card is already showing, so reveal the
         gallery only once it has actually decoded — otherwise the swap can
         land on an empty frame and read as a blink. */
      const cur = slides[idx];
      const show = () => lmc.classList.add('is-gal');
      if (cur && cur.decode) cur.decode().then(show, show);
      else show();
    }

    function hydrate() {
      for (let i = idx - 1; i <= idx + 1; i++) {
        const im = slides[i];
        if (im && !im.src && im.dataset.src) {
          im.src = im.dataset.src;
          /* Kick the decode now rather than when it becomes the active slide. */
          if (im.decode) im.decode().catch(() => {});
        }
      }
    }

    function paintDots() {
      const n = slides.length || list().length;
      if (dots.childElementCount !== n) {
        dots.textContent = '';
        for (let i = 0; i < n; i++) {
          const d = document.createElement('span');
          d.className = 'lmc__dot';
          dots.appendChild(d);
        }
      }
      [...dots.children].forEach((d, i) => d.classList.toggle('is-on', i === idx));
    }

    function goto(n, instant) {
      const max = slides.length - 1;
      if (max < 0) return;
      idx = n < 0 ? 0 : n > max ? max : n;
      if (instant) track.classList.add('is-dragging');
      track.style.transform = 'translate3d(' + -idx * 100 + '%,0,0)';
      if (instant) {
        void track.offsetWidth;
        track.classList.remove('is-dragging');
      }
      hydrate();
      paintDots();
    }

    async function step(delta) {
      setLive(true);
      await load();
      ensureGal();
      goto(idx + delta);
    }

    prev.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      step(-1);
    });
    next.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      step(1);
    });

    /* ---- swipe ----
       pan-y is declared in CSS, so the page keeps scrolling until
       the gesture proves itself horizontal. Only then is the move
       claimed, and only then is the card's own link suppressed. */
    let sx = 0;
    let sy = 0;
    let dx = 0;
    let axis = 0; /* 0 undecided, 1 horizontal, -1 vertical */
    let swiped = false;
    let width = 0;

    figure.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length !== 1) return;
        sx = e.touches[0].clientX;
        sy = e.touches[0].clientY;
        dx = 0;
        axis = 0;
        swiped = false;
        width = figure.clientWidth || 1; /* the only layout read, once per gesture */
        setLive(true);
        /* Build the track NOW. By the time the finger has travelled 8px the
           slides exist and the drag tracks the finger on the first frame. */
        load().then(() => {
          if (list().length > 1) ensureGal();
        });
      },
      { passive: true }
    );

    figure.addEventListener(
      'touchmove',
      (e) => {
        if (e.touches.length !== 1) return;
        const mx = e.touches[0].clientX - sx;
        const my = e.touches[0].clientY - sy;
        if (axis === 0) {
          if (Math.abs(my) > 8 && Math.abs(my) > Math.abs(mx)) {
            axis = -1;
            return;
          }
          if (Math.abs(mx) > 8 && Math.abs(mx) > Math.abs(my)) {
            /* Only claim the horizontal axis if there is a real track to drag.
               If the data still has not landed, do NOT half-start — hand the
               gesture back to the page rather than swallowing it into a drag
               that cannot move. */
            if (!built && model && list().length > 1) ensureGal(); /* 2026-09-04: data warmed near the viewport — build now, on this very move */
          if (!built || slides.length < 2) {
              axis = -1;
              return;
            }
            axis = 1;
            track.classList.add('is-dragging');
          lmc.classList.add('is-gal'); /* 2026-09-04: reveal on the first claimed swipe, not after a decode */
          } else {
            return;
          }
        }
        if (axis !== 1) return;
        e.preventDefault();
        dx = mx;
        swiped = Math.abs(mx) > 8;
        const pct = (dx / width) * 100;
        track.style.transform = 'translate3d(' + (-idx * 100 + pct) + '%,0,0)';
      },
      { passive: false }
    );

    const endSwipe = () => {
      if (axis === 1 && built && slides.length > 1) {
        track.classList.remove('is-dragging');
        if (Math.abs(dx) > Math.min(56, width * 0.18)) goto(idx + (dx < 0 ? 1 : -1));
        else goto(idx);
      }
      axis = 0;
      dx = 0;
      setLive(false);
    };
    figure.addEventListener('touchend', endSwipe, { passive: true });
    figure.addEventListener('touchcancel', endSwipe, { passive: true });

    /* A swipe must never open the PDP. */
    link.addEventListener(
      'click',
      (e) => {
        if (swiped) {
          e.preventDefault();
          e.stopPropagation();
          swiped = false;
        }
      },
      true
    );

    /* ---- quick add ---- */
    function paintSizes() {
      sizes.textContent = '';
      const c = model && model.colours[colour];
      if (!c) return;
      c.sizes.forEach((s) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'lmc__size';
        b.textContent = s.label;
        b.disabled = !s.available;
        b.setAttribute(
          'aria-label',
          (s.available ? 'Add ' : 'Sold out: ') +
            title +
            (c.name === '__only__' ? '' : ' ' + c.name) +
            ' size ' +
            s.label
        );
        if (s.available) {
          b.addEventListener('click', async () => {
            const ok = await addToCart(card, s.id, b, note, title);
            if (ok) {
              setTimeout(() => {
                b.classList.remove('is-added');
                if (panelOpen) closePanel(false);
              }, 1700);
            }
          });
        }
        sizes.appendChild(b);
      });
    }

    function openPanel() {
      panelOpen = true;
      lmc.classList.add('is-open');
      open.setAttribute('aria-expanded', 'true');
      paintSizes();
      note.textContent = 'Choose a size';
      const first = sizes.querySelector('.lmc__size:not(:disabled)');
      if (first) first.focus({ preventScroll: true });
    }

    function closePanel(refocus) {
      panelOpen = false;
      lmc.classList.remove('is-open');
      open.setAttribute('aria-expanded', 'false');
      note.textContent = '';
      if (refocus) open.focus({ preventScroll: true });
    }

    open.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const m = await load();
      if (!m) {
        note.textContent = 'Unavailable';
        return;
      }
      if (!m.hasSize) {
        const c = m.colours[colour];
        const only = c && c.sizes.find((s) => s.available);
        lmc.classList.add('is-open');
        panelOpen = true;
        sizes.textContent = '';
        if (!only) {
          note.textContent = 'Sold out';
          return;
        }
        const ok = await addToCart(card, only.id, null, note, title);
        setTimeout(() => closePanel(false), ok ? 1600 : 2400);
        return;
      }
      openPanel();
    });

    close.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      closePanel(true);
    });

    card.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && panelOpen) {
        e.stopPropagation();
        closePanel(true);
        return;
      }
      if (panelOpen) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        const t = e.target;
        /* the colour swatches are a radio group — their own arrow-key
           behaviour must win */
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) return;
        e.preventDefault();
        step(e.key === 'ArrowRight' ? 1 : -1);
      }
    });

    /* Leaving the card with a pointer tidies the panel away, but a
       keyboard user inside it is left alone. */
    card.addEventListener('mouseleave', () => {
      if (panelOpen && !card.contains(document.activeElement)) closePanel(false);
    });

    /* ---- intent ---- */
    const warm = () => load().then((m) => { if (TOUCH && m && list().length > 1) ensureGal(); return m; }); /* 2026-09-04: on touch, pre-build the track as the card nears the viewport so the FIRST swipe moves */
    card.addEventListener('pointerenter', warm, { once: true });
    card.addEventListener('focusin', warm, { once: true });
    /* touchstart already calls load() on every gesture, so the {once} warm there
       is redundant now. */
    card.addEventListener('pointerenter', () => setLive(true));
    card.addEventListener('pointerleave', () => setLive(false));

    card._lmcWarm = warm;
    if (nearObs) nearObs.observe(card);
  }


  /* ----------------------------------------------------------
     One SETS cell (homepage "Buy the whole look").
     2026-08-26. Same interaction grammar as the card gallery —
     swipe on touch with the 8px direction claim, quiet chevrons
     on pointer devices, dot row, lazy neighbour loading — and
     the same machinery: getProduct's shared cache, build()'s
     alt-tag colour grouping, the .lmc__* chrome. The cell IS
     the link, so the chevrons cancel the anchor default and a
     swipe suppresses the click via the same capture handler.
     No quick-add here: this section sells the pair.
     Rollback: assets/lemein-card-shop-2026-08-26-pre-swipe75.txt
     ---------------------------------------------------------- */
  const SET_STEPS = [300, 400, 500, 600, 700, 800, 1000, 1200];

  function enhanceSet(cell) {
    if (cell.dataset.lmc) return;
    cell.dataset.lmc = '1';

    const handle = (cell.getAttribute('data-handle') || '').trim();
    if (!handle) return;
    const wanted = (cell.getAttribute('data-colour') || '').trim().toLowerCase();
    const resting = cell.querySelector('img');

    const lmc = document.createElement('div');
    lmc.className = 'lmc lmc--set';

    const gal = document.createElement('div');
    gal.className = 'lmc__gal';
    const track = document.createElement('div');
    track.className = 'lmc__track';
    gal.appendChild(track);

    const chev = (d) =>
      '<svg viewBox="0 0 9 16" fill="none" aria-hidden="true"><path d="' +
      d +
      '" stroke="currentColor" stroke-width="1.2"/></svg>';
    /* Out of the tab order for the same reason as on the cards:
       keyboard users step the gallery with Left/Right while the
       cell link has focus. */
    const prev = document.createElement('button');
    prev.type = 'button';
    prev.tabIndex = -1;
    prev.setAttribute('aria-hidden', 'true');
    prev.className = 'lmc__arrow lmc__arrow--prev';
    prev.innerHTML = chev('M7.5 1 1 8l6.5 7');
    const next = document.createElement('button');
    next.type = 'button';
    next.tabIndex = -1;
    next.setAttribute('aria-hidden', 'true');
    next.className = 'lmc__arrow lmc__arrow--next';
    next.innerHTML = chev('M1.5 1 8 8l-6.5 7');

    const dots = document.createElement('div');
    dots.className = 'lmc__dots';

    lmc.append(gal, prev, next, dots);
    cell.appendChild(lmc);

    let images = null;
    let idx = 0;
    let slides = [];
    let built = false;
    let bucket = 0;
    const setLive = mkLive(lmc);

    const sizedSet = (url) =>
      url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + bucket;

    const samePath = (a, b) => {
      const p = (u) => {
        try {
          /* 2026-09-04: the section serves images from lemein.co/cdn/shop/files/... while the
             product .js lists the SAME file at cdn.shopify.com/s/files/... — comparing full
             pathnames never matched, so the card's own front was inserted ahead of the product's
             front and every set piece showed two fronts. Compare on the filename only, ignoring
             the responsive size suffix (_600x, _1200x800) and the query string. */
          return new URL(u, location.origin).pathname.split('/').pop().replace(/_\d+x\d*(?=\.\w+$)/, '').toLowerCase();
        } catch (e) {
          return u;
        }
      };
      return p(a) === p(b);
    };

    function paintDots() {
      const n = slides.length || (images ? images.length : 0);
      if (dots.childElementCount !== n) {
        dots.textContent = '';
        for (let i = 0; i < n; i++) {
          const d = document.createElement('span');
          d.className = 'lmc__dot';
          dots.appendChild(d);
        }
      }
      [...dots.children].forEach((d, i) => d.classList.toggle('is-on', i === idx));
    }

    let loading = null;
    function load() {
      if (images) return Promise.resolve(images);
      if (!loading) {
        loading = getProduct(handle).then((p) => {
          if (!p || !p.variants || !p.variants.length) return null;
          /* One read, on intent, never in a scroll path. */
          const w = cell.clientWidth || 340;
          bucket = SET_STEPS.find((s) => s >= w * dpr) || 1400;
          const model = build(p);
          let entry = null;
          if (wanted) {
            entry = model.colours.find(
              (c) => String(c.name).toLowerCase() === wanted
            );
          }
          if (!entry) entry = model.colours[0];
          let list = entry && entry.images.length ? entry.images.slice() : [];
          if (!list.length) {
            list = (p.media || [])
              .filter((m) => m.media_type === 'image')
              .map((m) => m.src || (m.preview_image && m.preview_image.src))
              .filter(Boolean);
          }
          /* Continuity: the gallery keeps the product's own order
             and simply STARTS on whatever image the section
             rendered (variant image or editor override). */
          const cur = resting && (resting.currentSrc || resting.src);
          if (cur) {
            const at = list.findIndex((u) => samePath(u, cur));
            if (at >= 0) idx = at;
            else if (list.length) idx = 0; /* product has images for this colour: its own front IS the front */
            else {
              list.unshift(cur.split('?')[0]);
              idx = 0;
            }
          }
          images = list;
          if (images.length > 1) {
            lmc.classList.add('has-gal');
            paintDots();
          }
          return images;
        });
      }
      return loading;
    }

    function hydrate() {
      for (let i = idx - 1; i <= idx + 1; i++) {
        const im = slides[i];
        if (im && !im.src && im.dataset.src) {
          im.src = im.dataset.src;
          if (im.decode) im.decode().catch(() => {});
        }
      }
    }

    function goto(n, instant) {
      const max = slides.length - 1;
      if (max < 0) return;
      idx = n < 0 ? 0 : n > max ? max : n;
      if (instant) track.classList.add('is-dragging');
      track.style.transform = 'translate3d(' + -idx * 100 + '%,0,0)';
      if (instant) {
        void track.offsetWidth;
        track.classList.remove('is-dragging');
      }
      hydrate();
      paintDots();
    }

    function ensureGal() {
      if (built) return;
      built = true;
      slides = images.map((src, i) => {
        const s = document.createElement('div');
        s.className = 'lmc__slide';
        const im = document.createElement('img');
        im.loading = Math.abs(i - idx) <= 1 ? 'eager' : 'lazy';
        im.decoding = 'async';
        im.alt = '';
        im.dataset.src = (i === idx && resting && resting.currentSrc && sameFile(resting.currentSrc, src)) ? resting.currentSrc : sizedSet(src);
        s.appendChild(im);
        track.appendChild(s);
        return im;
      });
      paintDots();
      goto(idx, true);
      const cur = slides[idx];
      const show = () => lmc.classList.add('is-gal');
      if (cur && cur.decode) cur.decode().then(show, show);
      else show();
    }

    async function step(delta) {
      setLive(true);
      const list = await load();
      if (!list || list.length < 2) return;
      ensureGal();
      goto(idx + delta);
    }

    prev.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      step(-1);
    });
    next.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      step(1);
    });

    /* ---- swipe: the same 8px direction claim as the cards ---- */
    let sx = 0;
    let sy = 0;
    let dx = 0;
    let axis = 0;
    let swiped = false;
    let width = 0;

    cell.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length !== 1) return;
        sx = e.touches[0].clientX;
        sy = e.touches[0].clientY;
        dx = 0;
        axis = 0;
        swiped = false;
        width = cell.clientWidth || 1; /* the only layout read, once per gesture */
        setLive(true);
        load().then((list) => {
          if (list && list.length > 1) ensureGal();
        });
      },
      { passive: true }
    );

    cell.addEventListener(
      'touchmove',
      (e) => {
        if (e.touches.length !== 1) return;
        const mx = e.touches[0].clientX - sx;
        const my = e.touches[0].clientY - sy;
        if (axis === 0) {
          if (Math.abs(my) > 8 && Math.abs(my) > Math.abs(mx)) {
            axis = -1;
            return;
          }
          if (Math.abs(mx) > 8 && Math.abs(mx) > Math.abs(my)) {
            /* Same rule as the cards: never claim the axis without a track. */
            if (!built && images && images.length > 1) ensureGal();
          if (!built || slides.length < 2) {
              axis = -1;
              return;
            }
            axis = 1;
            track.classList.add('is-dragging');
          lmc.classList.add('is-gal'); /* 2026-09-04: reveal on the first claimed swipe, not after a decode */
          } else {
            return;
          }
        }
        if (axis !== 1) return;
        e.preventDefault();
        dx = mx;
        swiped = Math.abs(mx) > 8;
        track.style.transform =
          'translate3d(' + (-idx * 100 + (dx / width) * 100) + '%,0,0)';
      },
      { passive: false }
    );

    const endSwipe = () => {
      if (axis === 1 && built && slides.length > 1) {
        track.classList.remove('is-dragging');
        if (Math.abs(dx) > Math.min(56, width * 0.18)) goto(idx + (dx < 0 ? 1 : -1));
        else goto(idx);
      }
      axis = 0;
      dx = 0;
      setLive(false);
    };
    cell.addEventListener('touchend', endSwipe, { passive: true });
    cell.addEventListener('touchcancel', endSwipe, { passive: true });

    /* A swipe must never open the product page. */
    cell.addEventListener(
      'click',
      (e) => {
        if (swiped) {
          e.preventDefault();
          e.stopPropagation();
          swiped = false;
        }
      },
      true
    );

    /* Keyboard twin of the swipe, as on the cards. */
    cell.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        step(e.key === 'ArrowRight' ? 1 : -1);
      }
    });

    /* Data on intent only. */
    const warm = () => load().then((l) => { if (TOUCH && l && l.length > 1) ensureGal(); return l; });
    cell.addEventListener('pointerenter', warm, { once: true });
    cell.addEventListener('focusin', warm, { once: true });
    cell.addEventListener('pointerenter', () => setLive(true));
    cell.addEventListener('pointerleave', () => setLive(false));

    cell._lmcWarm = warm;
    if (nearObs) nearObs.observe(cell);
  }

  /* ----------------------------------------------------------
     Boot
     ---------------------------------------------------------- */
  const scan = (root) => {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('product-card').forEach(enhance);
    root.querySelectorAll('.lm-set__cell[data-handle]').forEach(enhanceSet);
  };

  function start() {
    scan(document);

    /* nearObs handles this on any modern browser. Without it, fall back to the
       old behaviour: warm the first four on idle. */
    if (!nearObs) {
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 900));
      idle(() => {
        [...document.querySelectorAll('product-card')].slice(0, 4).forEach((c) => {
          if (c._lmcWarm) c._lmcWarm();
        });
      });
    }

    new MutationObserver((muts) => {
      muts.forEach((m) =>
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.tagName === 'PRODUCT-CARD') enhance(n);
          else scan(n);
        })
      );
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
